/**
 * One peer connection, and the media that goes into it.
 *
 * Everything here is about a live conversation, which is a different problem
 * from the recordings in capture.ts and wants the opposite settings in several
 * places — see openMicrophone below, which is the one that matters most.
 *
 * The signalling that carries these bytes is somebody else's job: this module
 * produces offers, answers and candidates, and hands them to a callback. See
 * CallContext.
 */

import type { CallCodec, CallTuning, IceConfig } from '../api/client.ts';
import {
  describeConnection,
  describeMedia,
  gatheringNote,
  iceErrorNotes,
  qualityNote,
  summariseConnection,
  summariseMedia,
  tallyCandidates,
  type CallNote,
  type IceErrorRecord,
  type StatEntry,
} from './callDiagnostics.ts';
import { cameraOf, describeCamera, flipVerdict } from './cameraFlip.ts';
import type { Facing } from './capture.ts';
import { describeOpus, withOpusOptions } from './opus.ts';

export type { IceConfig };

/** What travels over `call:signal`. The server forwards it without looking. */
export type SignalPayload =
  | { kind: 'offer'; sdp: RTCSessionDescriptionInit }
  | { kind: 'answer'; sdp: RTCSessionDescriptionInit }
  | { kind: 'candidate'; candidate: RTCIceCandidateInit }
  // Sent when this side turns its camera on or off, because a disabled track
  // still arrives as a live track at the other end — muting is local, and the
  // remote video would simply freeze on its last frame with no explanation.
  | { kind: 'camera'; on: boolean }
  // «My paths are gone, offer me new ones.» Only the callee ever sends it: an
  // ICE restart is a fresh offer, the caller is the only side that offers, and
  // that asymmetry is what saves this from needing glare handling. See restart.
  | { kind: 'restart' };

export interface PeerHandlers {
  /** Hand this to the other side. Called for offers, answers and every candidate. */
  onSignal: (payload: SignalPayload) => void;
  /** The other side's audio and video, as one stream. Fires once. */
  onRemoteStream: (stream: MediaStream) => void;
  /** True while the media path is up; false during a blip, before a retry. */
  onConnectionChange: (state: RTCPeerConnectionState) => void;
  /** The far end turned its camera on or off. */
  onRemoteCamera: (on: boolean) => void;
  /**
   * Something about this connection is worth writing down. Handed out rather
   * than filed here for the same reason signalling is: this module knows what
   * WebRTC said, and the caller knows which call it was said about.
   */
  onDiagnostic: (note: CallNote) => void;
}

/**
 * The microphone, asked for the way a *call* wants it.
 *
 * Echo cancellation is on, and this is the whole reason this function exists
 * rather than reusing capture.ts's `openMicrophone`. That one turns it off
 * deliberately — a voice message has no far end playing back, so the processing
 * can only take away. A call is the exact opposite case: without cancellation
 * the other person hears themselves come back out of this device's speaker a
 * beat later, which is the single most unpleasant thing a call can do.
 *
 * The other two are on for both: they are what make a phone held at arm's length
 * in a kitchen intelligible.
 */
export function openCallMicrophone(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
}

/**
 * What a call is set to when nobody has said otherwise.
 *
 * These are the numbers that used to be constants in this file, and they stay
 * here as the fallback rather than moving to the server alone: a device whose
 * /api/calls/ice answer is older than the panel — or a test with no server at
 * all — still gets a working call rather than an undefined width. The live
 * values come from the admin page and travel with the ICE credentials.
 *
 * 640×480 at 500 kbps rather than the largest thing the camera offers: this is
 * a moving picture over somebody's mobile data, and the encoder will spend
 * whatever resolution it is given.
 */
export const DEFAULT_CALL_TUNING: CallTuning = {
  callWidth: 640,
  callHeight: 480,
  callFps: 24,
  callVideoKbps: 500,
  callAudioKbps: 40,
  callDegradation: 'balanced',
  callCodec: 'auto',
  callAudioFec: true,
  callRelayFirst: false,
  callStatsSeconds: 5,
};

/** What this call is set to: the server's numbers, or the built-in ones. */
export function callTuning(config: Pick<IceConfig, 'tunables'> | null): CallTuning {
  return { ...DEFAULT_CALL_TUNING, ...(config?.tunables ?? {}) };
}

/**
 * The capture constraints, as `ideal` rather than `exact` so that a camera
 * without that mode picks its nearest instead of refusing outright — which is
 * the difference between a slightly smaller picture and a call that cannot
 * start. The frame rate keeps a hard ceiling: over 30 nothing here is feeding
 * it, and a camera that offers 60 would spend the bitrate on frames nobody
 * asked for.
 */
function videoConstraints(tuning: CallTuning, facing?: Facing): MediaTrackConstraints {
  return {
    ...(facing === undefined ? {} : { facingMode: facing }),
    width: { ideal: tuning.callWidth },
    height: { ideal: tuning.callHeight },
    frameRate: { ideal: tuning.callFps, max: Math.max(tuning.callFps, 30) },
  };
}

/**
 * Camera and microphone for a call.
 *
 * The tuning is optional because the camera is opened at the tap — before the
 * outgoing screen exists, and sometimes before this device has fetched the ICE
 * answer the numbers ride on. A capture opened without them is corrected the
 * moment the connection is built (see retune), which costs nothing: constraints
 * on a running track do not restart it.
 */
export async function openCallMedia(
  video: boolean,
  facing: Facing,
  tuning: CallTuning = DEFAULT_CALL_TUNING,
): Promise<MediaStream> {
  if (!video) return openCallMicrophone();
  return navigator.mediaDevices.getUserMedia({
    video: videoConstraints(tuning, facing),
    audio: {
      channelCount: 1,
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  });
}

/**
 * One camera, opened to the given constraints.
 *
 * A grant that comes back without a video track is a refusal wearing a
 * resolved promise: the flip paths all branch on «did this throw», and a
 * stream with nothing in it must reach them as the failure it is rather than
 * as a track that is undefined three lines later.
 */
async function openCameraTrack(video: MediaTrackConstraints): Promise<MediaStreamTrack> {
  const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
  const track = stream.getVideoTracks()[0];
  if (!track) throw new Error('the camera opened with no picture in it');
  return track;
}

/**
 * Puts the tuned codec first, leaving everything else in the order the browser
 * gave. Pure, and exported for that reason: what a codec list should look like
 * is the sort of thing worth having tests about rather than a live call.
 *
 * 'auto' and a codec this browser does not have both mean «leave it alone» —
 * an offer without VP9 in it is not improved by an empty preference list. The
 * entries that are not codecs at all (rtx, red, ulpfec) travel with the rest:
 * dropping them would take retransmission and error correction with them.
 */
export function orderCodecs<T extends { mimeType: string }>(
  codecs: readonly T[],
  preferred: CallCodec,
): T[] {
  if (preferred === 'auto') return [...codecs];
  const wanted = `video/${preferred}`.toLowerCase();
  const first = codecs.filter((codec) => codec.mimeType.toLowerCase() === wanted);
  if (first.length === 0) return [...codecs];
  return [...first, ...codecs.filter((codec) => codec.mimeType.toLowerCase() !== wanted)];
}

/**
 * How many times a failing connection is given fresh ICE credentials.
 *
 * Three, because the case a restart recovers is a network that changed — one
 * attempt lands it — and the case it cannot is a network that is gone, where
 * every further attempt is two phones offering at each other until somebody
 * hangs up.
 *
 * Exported for the same reason as the bitrate caps: the restart policy is the
 * page's on both implementations of CallConnection, and one number keeps the
 * two from disagreeing about when a network is gone.
 */
export const MAX_RESTARTS = 3;

/**
 * How long the caller waits for a relayed path before allowing a direct one.
 *
 * Only with «сначала через релей» on. The order is deliberately relay first
 * and direct second rather than the other way round: a browser never moves a
 * working call onto a relay — the relay pair has the lowest priority there is
 * — so the only way to *use* the relay is to forbid the direct path from the
 * start, and the only way to keep a call from being lost to an unreachable
 * relay is to lift that ban in time. Six seconds from the answer: a relayed
 * pair connects in one or two when the relay answers at all, and the whole
 * cost of a wrong guess is that many seconds of silence after «принял».
 *
 * Counted from the answer, not from the offer, because until the far end has
 * answered there is nothing to connect to and the wait would measure the
 * ringing.
 */
export const RELAY_GRACE_MS = 6000;

/**
 * How long audio gets to start flowing before a zero is believed.
 *
 * Counted from the moment sound *should* have started — the connection coming
 * up, or the system handing a suspended capture back — not from the start of
 * the call. A working microphone puts kilobytes on the wire within the first
 * second of either; two and a half is past every warm-up the device passes
 * measured, so a zero here is a verdict rather than impatience.
 */
const SILENT_MIC_GRACE_MS = 2500;

/**
 * How many fresh captures a silent microphone is offered.
 *
 * Two: one for the ordinary case and one for a restart that landed while the
 * session was still being fought over. A microphone that two fresh captures
 * could not start is not going to be started by a third, and each attempt
 * closes the previous track — looping would turn a quiet call into a busy one.
 */
const MAX_MIC_RESTARTS = 2;

/**
 * Caps what one sender may cost, and says what it should give up when it cannot
 * hold that.
 *
 * Applied through the sender rather than by rewriting the SDP: it is the
 * supported way, it can be changed mid-call, and it does not involve parsing a
 * format nobody should be parsing by hand. Both settings go in one
 * `setParameters` because each of them costs a round trip through the encoder,
 * and because a cap applied without its degradation preference is a call that
 * spends a moment deciding what to drop with the old rule.
 */
async function capBitrate(
  sender: RTCRtpSender,
  max: number,
  degradation?: RTCDegradationPreference,
): Promise<void> {
  const parameters = sender.getParameters();
  /**
   * A sender that has not negotiated yet has no encodings, and this used to
   * invent one — `parameters.encodings = [{}]` — so that the cap would apply
   * from the first frame. That is an invalid `setParameters`: the standard
   * allows changing the *contents* of the encodings it handed out and not their
   * number, and WebKit's answer to it was measured on two phones and an iPad.
   * The audio sender of whichever side had *placed* the call went on to send
   * exactly zero bytes for the length of the call, with a track that reported
   * itself live and unmuted throughout and a picture that arrived perfectly.
   * The caller was the one hit because it is the one that capped before its
   * answer came back — video senders already have an encoding by then and
   * audio senders do not.
   *
   * So: cap what exists and leave the rest alone. An uncapped call is a worse
   * call, not a broken one — the same judgement the catch below makes.
   */
  if (!parameters.encodings || parameters.encodings.length === 0) return;
  for (const encoding of parameters.encodings) encoding.maxBitrate = max;
  // Only where it means something: a preference belongs to a picture that can
  // be made smaller or slower, and audio has neither dimension to give up.
  if (degradation !== undefined) parameters.degradationPreference = degradation;
  try {
    await sender.setParameters(parameters);
  } catch {
    // Firefox rejects setParameters in some states. A call at an uncapped
    // bitrate is a worse call, not a broken one, so this is not worth failing.
  }
}

/**
 * What CallContext asks of a live connection, wherever the connection lives.
 *
 * Two implementations: PeerCall below, which is WebKit's RTCPeerConnection and
 * runs everywhere, and NativeCallPeer in lib/nativeCallPeer.ts, which is
 * libwebrtc inside the iOS shell — the only environment where a page-side
 * connection was measured to lose its microphone under CallKit. The interface
 * is PeerCall's shape, with one widening: `flipCamera` may resolve with null,
 * because the native connection renders its own preview and has no stream to
 * hand back to a `<video>`.
 */
export interface CallConnection {
  /** The caller's opening move. The callee never calls this. */
  start(): Promise<void>;
  /** Everything that arrives over `call:signal`, in one door. */
  accept(payload: SignalPayload): Promise<void>;
  /** Mute, or stop sending a picture. Local and instant; no renegotiation. */
  setTrackEnabled(kind: 'audio' | 'video', enabled: boolean): void;
  /**
   * Null when the picture is drawn outside the page and there is nothing to
   * bind. Rejects when the camera did not turn round — refused, busy, or
   * quietly the same one back — which is what the caller undoes its mirror on.
   */
  flipCamera(facing: Facing): Promise<MediaStream | null>;
  describe(): Promise<string>;
  quality(): Promise<CallNote | null>;
  audioBytesSent(): Promise<number | null>;
  /**
   * Everything `getStats` knows, flattened, for whoever wants to do their own
   * arithmetic on it — which is the telemetry reporter (lib/callTelemetry.ts).
   * Empty rather than throwing when the connection will not answer.
   */
  stats(): Promise<StatEntry[]>;
  close(): void;
  readonly connectionState: RTCPeerConnectionState;
}

/**
 * A live call's connection.
 *
 * Constructed by both sides; only the caller calls `start()`. The difference
 * between the two roles is that small because the callee learns everything it
 * needs from the offer it is handed.
 */
export class PeerCall implements CallConnection {
  private pc: RTCPeerConnection;
  private local: MediaStream;
  private remote = new MediaStream();
  private handlers: PeerHandlers;
  private remoteAnnounced = false;
  /**
   * Candidates that arrived before there was a remote description to attach them
   * to. Trickle ICE means the other side starts sending them the instant it has
   * any, which can be before its offer has finished crossing the wire; adding
   * one early throws, and the connection then quietly loses a path it needed.
   */
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private closed = false;
  /** Whether this deployment has a relay at all; see the gathering check below. */
  private relayConfigured: boolean;
  /** The type of every candidate this side gathered, for that same check. */
  private gathered: string[] = [];
  /**
   * The local addresses that produced a srflx or relay candidate — the
   * interfaces of this machine that can actually reach an ICE server. See
   * iceErrorNotes: it is what tells a relay that is down from an adapter that
   * was never going anywhere.
   */
  private reachedFrom = new Set<string>();
  /** ICE errors waiting for the end of gathering, which is when they can be judged. */
  private pendingIceErrors: IceErrorRecord[] = [];
  /** ICE servers that answered with an error, so a missing relay can say why. */
  private iceErrors = 0;
  /** Candidates the connection would not take. One is normal; all of them is not. */
  private rejectedCandidates = 0;
  /** How many times ICE has been restarted, which separates a blip from a wall. */
  private restarts = 0;
  /**
   * Whether this side is the one that offers. Set by `start()`, which only the
   * caller calls — see the restart path, which is the only thing that reads it.
   */
  private caller = false;
  /** So the «that was the last one» line is written once rather than per failure. */
  private restartsExhausted = false;
  /** One camera flip at a time, however many times the button is pressed. */
  private flipping = false;
  /** Fresh captures already spent on a silent microphone; see checkMicrophone. */
  private micRestarts = 0;
  /** The pending «did audio actually start?» question, re-armed rather than stacked. */
  private micCheckTimer: ReturnType<typeof setTimeout> | null = null;
  /** What this call is set to: the server's numbers, or the built-in ones. */
  private tuning: CallTuning;
  /** So the Opus line is one per call rather than one per restart. */
  private audioDescribed = false;
  /** For setConfiguration, which wants the whole configuration again. */
  private readonly iceServers: RTCIceServer[];
  /**
   * Where the relay-first experiment stands on this side. `off` on every
   * callee and on every call with the switch off; `trying` while the direct
   * path is forbidden and nothing has connected; `held` once the relayed pair
   * carries the call; `fallen` after the ban was lifted. See allowDirect.
   */
  private relayPhase: 'off' | 'trying' | 'held' | 'fallen' = 'off';
  private relayTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    config: IceConfig,
    local: MediaStream,
    handlers: PeerHandlers,
    options: { caller?: boolean } = {},
  ) {
    this.local = local;
    this.handlers = handlers;
    this.relayConfigured = config.hasRelay;
    this.tuning = callTuning(config);
    this.iceServers = config.iceServers;
    // The caller alone forbids its direct path, and that is enough: with only
    // relay candidates on one side, the pair has to go through coturn in both
    // directions, and the callee needs no allocation of its own — it sends to
    // the relay's public address like to any other. One side forbidding also
    // means one side falling back, and no glare between two.
    const relayFirst = this.tuning.callRelayFirst && options.caller === true;
    this.relayPhase = relayFirst ? 'trying' : 'off';
    this.pc = new RTCPeerConnection({
      iceServers: config.iceServers,
      // 'relay' forbids host and srflx candidates outright, so the media
      // cannot take the direct path even when one exists — which is the whole
      // instrument: the samples then say whether the loss lived on the
      // sender's own link or on the peering the relay bypasses. Same policy
      // the relay check in the settings uses, for the same reason.
      iceTransportPolicy: relayFirst ? 'relay' : 'all',
      // Pools a candidate before anyone asks, which shaves a beat off the first
      // offer — the one moment of a call where latency is visible as silence.
      iceCandidatePoolSize: 1,
    });

    for (const track of local.getTracks()) {
      this.tune(track);
      this.pc.addTrack(track, local);
      this.watchTrack(track);
    }
    // Before the first offer, which is the only moment a preference can be
    // expressed: setCodecPreferences reorders what goes *into* an SDP, and by
    // the time there is one the choice has been made.
    this.preferCodec();

    this.pc.addEventListener('icecandidate', (event) => {
      if (!event.candidate) return;
      this.gathered.push(event.candidate.type ?? '');
      // relatedAddress on a *srflx* candidate is the local address it was
      // gathered from — the one interface fact a browser gives away, and what
      // the ICE errors below are judged against. Only srflx: on a relay
      // candidate the same field holds the reflexive address instead of the
      // base, which would put a public address in this set and quietly make it
      // match nothing.
      if (event.candidate.type === 'srflx' && event.candidate.relatedAddress) {
        this.reachedFrom.add(event.candidate.relatedAddress);
      }
      handlers.onSignal({ kind: 'candidate', candidate: event.candidate.toJSON() });
    });

    // Fired per ICE server that would not serve this device. It is the only
    // place a browser ever says *why* a relay produced nothing — everything
    // downstream of it just sees a call that will not connect.
    this.pc.addEventListener('icecandidateerror', (event) => {
      const error = event as RTCPeerConnectionIceErrorEvent;
      // Held rather than filed. Whether this says anything about the relay
      // depends on what the other interfaces managed, and that is not known
      // until gathering ends — see flushIceErrors.
      this.pendingIceErrors.push({
        errorCode: error.errorCode,
        errorText: error.errorText,
        url: error.url,
        address: error.address,
      });
    });

    // Gathering finishing is the moment the question "did the relay allocate?"
    // has an answer, and it has one whether or not the call goes on to work.
    this.pc.addEventListener('icegatheringstatechange', () => {
      if (this.pc.iceGatheringState !== 'complete') return;
      this.flushIceErrors();
      const tally = tallyCandidates(this.gathered);
      const note = gatheringNote(tally, this.relayConfigured, this.iceErrors);
      if (note) handlers.onDiagnostic(note);
      // No relay candidate under a relay-only policy is no candidate at all,
      // and waiting the grace out would be waiting for nothing.
      if (this.relayPhase === 'trying' && tally.relay === 0) {
        this.allowDirect('relay gathered nothing');
      }
    });

    this.pc.addEventListener('track', (event) => {
      // A track of this kind that has already ended goes first, and this is not
      // tidiness. A `<video>` renders the *first* video track of the stream it
      // is bound to, so a renegotiation that brings a second one leaves the
      // element painting a corpse — black picture, healthy call, nothing in any
      // statistic. The same reasoning holds for the audio element beside it.
      for (const stale of this.remote.getTracks()) {
        if (stale.kind === event.track.kind && stale.readyState === 'ended') {
          this.remote.removeTrack(stale);
        }
      }
      this.remote.addTrack(event.track);
      // Once, not per track: audio and video arrive as two events and the UI
      // wants one stream it can bind to a single element.
      if (!this.remoteAnnounced) {
        this.remoteAnnounced = true;
        handlers.onRemoteStream(this.remote);
      }
    });

    this.pc.addEventListener('connectionstatechange', () => {
      handlers.onConnectionChange(this.pc.connectionState);
      // The first moment sound could be flowing, so the first moment the
      // question in checkMicrophone has a meaningful answer.
      if (this.pc.connectionState === 'connected') {
        this.armMicCheck('connect');
        if (this.relayPhase === 'trying') this.holdRelay();
      }
      // 'failed' means ICE has given up on every path it found. A restart asks
      // for a fresh set, which is what recovers a call that changed network —
      // walking out of Wi-Fi onto mobile data is the ordinary case.
      if (this.pc.connectionState === 'failed') {
        // Under the ban, 'failed' is not believed before the grace runs out.
        // Filed as one short row rather than as call/failed with the whole
        // description: it happens on every relay-first call the native side
        // places, and a line that says «connection failed» about a call that
        // connected 200 ms later is the kind of finding that buries real ones.
        if (this.relayPhase === 'trying') {
          this.handlers.onDiagnostic({
            detail: 'call/relay/early-failed',
            message: 'ice failed before the relayed pair · waiting out the grace',
          });
          return;
        }
        // Before the restart, not after: a restart discards the candidate pairs
        // that failed, and those are the whole of what there is to look at.
        void this.reportFailure();
        // A failure after the relayed pair carried the call is real.
        // Measured on the native side: the first remote candidate to arrive
        // was the Android phone's private address, the relay refuses a
        // permission for those (denied-peer-ip), and with that the only pair
        // gone libwebrtc said `failed` — 0 ms after `connecting`, with the
        // far end's srflx candidate still in flight and a relayed pair one
        // second away. Lifting the ban on that word put the call on the
        // direct path the switch exists to avoid. So while trying, the timer
        // is the only judge; once the relayed pair carried the call and then
        // failed, that is a real failure and the ban comes off at once.
        if (this.relayPhase === 'held') {
          this.allowDirect('ice failed through the relay');
          return;
        }
        this.restart();
      }
    });
  }

  /**
   * The census in CallContext reads these two states twice; this is the
   * moment they change, which is the half a pair of readings cannot give.
   * `mute` on a track of one's own is the system taking the capture away —
   * and an `unmute` that arrives seconds later is the difference between
   * waiting the interruption out and having to reopen the device.
   * The kind is in the detail, not only in the message, because the
   * reporter throttles by detail: with one slug for both, a microphone
   * suspending silenced the camera doing the same thing a second later.
   * How long into the call it happened is already on every row — noteCall
   * appends the call's own age.
   *
   * A method rather than a loop in the constructor because a track handed to
   * the sender later — the fresh capture in restartMicrophone — needs the same
   * pair of ears on it.
   */
  /**
   * Applies the tuned capture to a track, wherever that track came from.
   *
   * The camera is opened at the tap, before this device may have fetched the
   * numbers (see openCallMedia), so the constraints are applied again here
   * where they are certain to be known. `applyConstraints` on a live track
   * re-negotiates the camera mode without restarting the track — no black
   * frame, nothing for the far end to notice — and a camera that cannot do the
   * asked-for mode keeps the one it has, which is why the failure is ignored.
   *
   * The content hint goes with it: 'motion' is what a talking head is, and it
   * tells an encoder deciding between a sharp still and a smooth picture which
   * of the two this is.
   */
  private tune(track: MediaStreamTrack): void {
    if (track.kind !== 'video') return;
    track.contentHint = 'motion';
    void track.applyConstraints(videoConstraints(this.tuning)).catch(() => {});
  }

  /**
   * Puts the tuned codec at the head of the video transceiver's list.
   *
   * A no-op at the default 'auto', and a no-op on a browser without the API —
   * Safari grew `setCodecPreferences` late, and a call there is a call at
   * whatever the browser prefers, which is what every call was until this
   * existed.
   */
  private preferCodec(): void {
    if (this.tuning.callCodec === 'auto') return;
    const capabilities = RTCRtpSender.getCapabilities?.('video');
    if (!capabilities) return;
    for (const transceiver of this.pc.getTransceivers()) {
      if (transceiver.sender.track?.kind !== 'video') continue;
      try {
        transceiver.setCodecPreferences?.(orderCodecs(capabilities.codecs, this.tuning.callCodec));
      } catch {
        // A codec list this connection will not take is not worth a failed
        // call: without the preference it negotiates whatever both sides have.
      }
    }
  }

  private watchTrack(track: MediaStreamTrack): void {
    track.addEventListener('mute', () => {
      this.handlers.onDiagnostic({
        detail: `call/local/suspended/${track.kind}`,
        message: `${track.kind} capture suspended by the system`,
      });
    });
    track.addEventListener('unmute', () => {
      this.handlers.onDiagnostic({
        detail: `call/local/resumed/${track.kind}`,
        message: `${track.kind} capture resumed`,
      });
      void this.rekindle(track);
      // The other moment sound should have started: the system handing back a
      // capture it had suspended. Both device passes put the caller here —
      // resumed on the twentieth second, still 0B on the fiftieth.
      if (track.kind === 'audio') this.armMicCheck('resume');
    });
  }

  /**
   * What the connection looks like right now, as one line.
   *
   * Public because the moment worth asking is not always one WebRTC announces:
   * a call that never connects and is hung up on by a person who has waited
   * long enough is the ordinary way this fails, and ICE gives up well after
   * they do. See CallContext, which asks on the way out.
   */
  async describe(): Promise<string> {
    // Read before the await, all of it. The connection carries on failing,
    // restarting and closing while getStats is in flight, and a line that
    // described the state it reached afterwards would say "1 ice restarts" about
    // the very failure that asked for it.
    const state = this.closed ? 'closed' : this.pc.iceConnectionState;
    const counters = [
      `ice ${state}`,
      ...(this.iceErrors > 0 ? [`${this.iceErrors} ice server errors`] : []),
      ...(this.rejectedCandidates > 0 ? [`${this.rejectedCandidates} candidates rejected`] : []),
      ...(this.restarts > 0 ? [`${this.restarts} ice restarts`] : []),
    ];
    if (this.closed) return ['connection closed', ...counters].join(' · ');

    const entries = await this.stats();
    // Dropped rather than emptied when there is nothing to measure: the row is
    // read by eye, and `· ·` is a worse answer than a shorter line.
    const media = describeMedia(summariseMedia(entries));
    return [
      describeConnection(summariseConnection(entries)),
      ...(media === null ? [] : [media]),
      ...counters,
    ].join(' · ');
  }

  /**
   * Whether the picture cost anything worth writing down, and what.
   *
   * Asked of a call that *worked*, which is the case nothing else here looks at
   * — see qualityNote, which decides what counts. Null for a call that had
   * nothing wrong with it, and null for a connection already closed, since the
   * statistics go with it.
   *
   * Like `describe`, this must be called before teardown: `getStats()` is issued
   * during the synchronous part of this method, so a caller that calls it and
   * then closes the connection in the same turn still gets a reading.
   */
  async quality(): Promise<CallNote | null> {
    if (this.closed) return null;
    return qualityNote(summariseMedia(await this.stats()));
  }

  /**
   * Bytes of sound this side has actually put on the wire, or null when there
   * is nothing to ask.
   *
   * Separate from `quality()` because it is asked *during* a call rather than
   * at the end of one. A microphone whose track is live and unmuted while its
   * sender has produced nothing is the failure two device passes ended in, and
   * waiting for the closing row to say so costs a whole call each time.
   */
  async audioBytesSent(): Promise<number | null> {
    if (this.closed) return null;
    return summariseMedia(await this.stats()).audioOut;
  }

  /** Everything `getStats` knows, flattened. Empty when it would not answer. */
  async stats(): Promise<StatEntry[]> {
    const entries: StatEntry[] = [];
    try {
      const report = await this.pc.getStats();
      report.forEach((value) => entries.push(value as StatEntry));
    } catch {
      // A connection that is going away can refuse this. Nothing to say then.
      return [];
    }
    return entries;
  }

  /**
   * File the ICE errors held since the last flush, minus the ones that turned
   * out to be about a dead interface rather than about a server.
   *
   * Called at the end of gathering, which is when the verdict exists, and again
   * on failure — a call that fails before gathering finishes is the one whose
   * errors are least worth losing, and by then nothing has reached anything, so
   * nothing is dropped.
   */
  private flushIceErrors(): void {
    const notes = iceErrorNotes(this.pendingIceErrors, this.reachedFrom);
    this.pendingIceErrors = [];
    // Counted as filed, not as fired, so `describe()` and the gathering note
    // both talk about the same errors this call's journal actually shows.
    this.iceErrors += notes.length;
    for (const note of notes) this.handlers.onDiagnostic(note);
  }

  private async reportFailure(): Promise<void> {
    this.flushIceErrors();
    // Deliberately not re-checking `closed` after the await: a connection that
    // failed and was torn down a moment later is the case this row exists for,
    // and the statistics were already read.
    const description = await this.describe();
    this.handlers.onDiagnostic({
      detail: 'call/failed',
      message: `connection failed · ${description}`,
    });
  }

  /** The caller's opening move. The callee never calls this. */
  async start(): Promise<void> {
    this.caller = true;
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    this.handlers.onSignal({ kind: 'offer', sdp: offer });
    // The caps wait for the answer; see the accept path. Capping here is capping
    // senders that have not been negotiated yet, which is where capBitrate's
    // account of a silent microphone begins.
  }

  /** Everything that arrives over `call:signal`, in one door. */
  async accept(payload: SignalPayload): Promise<void> {
    if (this.closed) return;

    if (payload.kind === 'offer') {
      await this.pc.setRemoteDescription(this.tuneAudio(payload.sdp));
      await this.drainCandidates();
      const answer = await this.pc.createAnswer();
      await this.pc.setLocalDescription(answer);
      this.handlers.onSignal({ kind: 'answer', sdp: answer });
      await this.applyBitrateCaps();
      return;
    }

    if (payload.kind === 'answer') {
      await this.pc.setRemoteDescription(this.tuneAudio(payload.sdp));
      await this.drainCandidates();
      if (this.relayPhase === 'trying' && this.relayTimer === null) {
        this.relayTimer = setTimeout(
          () => this.allowDirect(`nothing through the relay in ${RELAY_GRACE_MS / 1000}s`),
          RELAY_GRACE_MS,
        );
      }
      // The caller's turn to cap, and the symmetric moment to the callee's
      // above: both descriptions exist, so every sender has the encodings the
      // standard says are the only ones it may be given.
      await this.applyBitrateCaps();
      return;
    }

    if (payload.kind === 'camera') {
      this.handlers.onRemoteCamera(payload.on);
      return;
    }

    if (payload.kind === 'restart') {
      // Asked for by the side that cannot ask ICE itself. A callee that gets
      // this has misread its own role and must not answer it with an offer.
      if (this.caller) await this.reoffer();
      return;
    }

    // A candidate for a connection that has no remote description yet has
    // nowhere to go; it is kept and replayed by drainCandidates.
    if (this.pc.remoteDescription === null) {
      this.pendingCandidates.push(payload.candidate);
      return;
    }
    await this.addCandidate(payload.candidate);
  }

  /**
   * The far end's description, with what this side's Opus encoder needs to
   * hear.
   *
   * The remote description rather than our own offer, which is the
   * counter-intuitive half of it: an encoder is configured by what the *other*
   * end said it wants, so this is the only place from which our own audio can
   * be told to carry forward error correction. See lib/opus.ts.
   *
   * The line is filed once per call, and it says what was negotiated rather
   * than what was asked for — «did the two ends agree on FEC» is a question no
   * statistic answers.
   */
  private tuneAudio(sdp: RTCSessionDescriptionInit): RTCSessionDescriptionInit {
    if (sdp.sdp === undefined) return sdp;
    const tuned = withOpusOptions(sdp.sdp, {
      fec: this.tuning.callAudioFec,
      maxAverageBitrate: this.tuning.callAudioKbps * 1000,
    });
    if (!this.audioDescribed) {
      this.audioDescribed = true;
      const line = describeOpus(tuned);
      if (line) this.handlers.onDiagnostic({ detail: 'call/audio/opus', message: line });
    }
    return { ...sdp, sdp: tuned };
  }

  private async drainCandidates(): Promise<void> {
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const candidate of queued) await this.addCandidate(candidate);
  }

  private async addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await this.pc.addIceCandidate(candidate);
    } catch {
      // One unusable candidate is normal — they are offered speculatively and
      // most of them lose. Only losing all of them matters, which is why this
      // is counted rather than reported: the count goes into the line that a
      // connection actually failing writes.
      this.rejectedCandidates += 1;
    }
  }

  private async applyBitrateCaps(): Promise<void> {
    for (const sender of this.pc.getSenders()) {
      if (!sender.track) continue;
      const video = sender.track.kind === 'video';
      await capBitrate(
        sender,
        (video ? this.tuning.callVideoKbps : this.tuning.callAudioKbps) * 1000,
        video ? this.tuning.callDegradation : undefined,
      );
    }
  }

  /**
   * Hands a sender its own track back, after the system returned the capture.
   *
   * Measured, not supposed: on the first device pass an iPad opened its
   * microphone suspended, WebKit unmuted the track twenty seconds in, and the
   * sender went on producing nothing for the remaining thirty — `aout 0B` on a
   * call whose camera, attached in the same breath and suspended the same way,
   * recovered by itself. A sender handed a track that was muted at the time can
   * stay attached to a source that never started, and `replaceTrack` is the
   * supported way to make it take the source again: no new offer, no
   * renegotiation, nothing for the far end to notice. It is the same lever
   * flipCamera uses, for the same reason.
   *
   * On every unmute rather than only after a suspension, because that keeps the
   * rule one sentence and re-handing a healthy sender its own track is nothing.
   *
   * Two later passes measured its limit: for the microphone this was not
   * enough — same track back, still 0B — which is what checkMicrophone
   * escalates past with a capture opened anew.
   */
  private async rekindle(track: MediaStreamTrack): Promise<void> {
    if (this.closed) return;
    const sender = this.pc.getSenders().find((candidate) => candidate.track?.kind === track.kind);
    if (!sender) return;
    try {
      await sender.replaceTrack(track);
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: `call/local/rekindle/${track.kind}`,
        message: `sender would not retake the ${track.kind} track · ${
          error instanceof Error ? error.name : 'unknown'
        }`,
      });
    }
  }

  /**
   * Schedules the one question that catches a dead sender: bytes on the wire, a
   * beat after the moment audio should have started flowing. Re-armed rather
   * than stacked, because the triggers overlap — a capture resumed a second
   * after the connection came up should be asked once, about its latest state.
   */
  private armMicCheck(after: string): void {
    if (this.closed || this.micRestarts >= MAX_MIC_RESTARTS) return;
    if (this.micCheckTimer !== null) clearTimeout(this.micCheckTimer);
    this.micCheckTimer = setTimeout(() => {
      this.micCheckTimer = null;
      void this.checkMicrophone(after);
    }, SILENT_MIC_GRACE_MS);
  }

  /**
   * A live, unmuted, deliberately-unmuted microphone whose sender has put
   * nothing on the wire is the failure five device passes ended in — on the
   * side CallKit performed a call action for, every time — and neither of the
   * gentler levers moved it: `rekindle` re-handing the sender its own track
   * changed nothing on two passes, and so did keeping bitrate caps away from
   * unnegotiated senders. Only a healthy-looking track indicts its sender; a
   * muted or ended one is the capture's own fault and has its own rows.
   */
  private async checkMicrophone(after: string): Promise<void> {
    if (this.closed || this.micRestarts >= MAX_MIC_RESTARTS) return;
    const track = this.local.getAudioTracks()[0];
    if (!track || track.readyState !== 'live' || track.muted || !track.enabled) return;
    // Null is «could not ask», not a finding — same rule as silentSenderNote.
    if ((await this.audioBytesSent()) !== 0) return;
    await this.restartMicrophone(after);
  }

  /**
   * Opens the microphone afresh and hands the sender the new track.
   *
   * A new acquisition rather than another `replaceTrack` of the same track,
   * because the same track is the thing that was measured not to work: the
   * suspected mechanism is CallKit activating the audio session over a capture
   * WebKit had already started, after which the track reports itself live while
   * the unit feeding the encoder never runs again. A fresh `getUserMedia` is
   * the only lever a page has that builds that unit anew — acquired before the
   * old track is stopped, so a refusal leaves the call no worse than it was.
   */
  private async restartMicrophone(after: string): Promise<void> {
    this.micRestarts += 1;
    this.handlers.onDiagnostic({
      detail: 'call/local/mic-restart',
      message: `sender sent 0B with a live microphone · reopening · after ${after} · attempt ${this.micRestarts}`,
    });
    let fresh: MediaStream;
    try {
      fresh = await openCallMicrophone();
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: 'call/local/mic-restart/failed',
        message: `fresh microphone refused · ${error instanceof Error ? error.name : 'unknown'}`,
      });
      return;
    }
    const track = fresh.getAudioTracks()[0];
    if (!track) return;
    if (this.closed) {
      // The call ended while the microphone was opening; teardown has already
      // run and nobody else will stop this track.
      track.stop();
      return;
    }
    const old = this.local.getAudioTracks()[0];
    // The person's own mute is a choice and travels to the new track.
    track.enabled = old?.enabled ?? true;
    this.watchTrack(track);
    const sender = this.pc.getSenders().find((candidate) => candidate.track?.kind === 'audio');
    try {
      await sender?.replaceTrack(track);
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: 'call/local/mic-restart/failed',
        message: `sender would not take the fresh track · ${
          error instanceof Error ? error.name : 'unknown'
        }`,
      });
      track.stop();
      return;
    }
    // Into the same stream, the way flipCamera swaps the picture: teardown
    // stops whatever the stream holds, so the fresh track is owned the same
    // way the one it replaces was.
    if (old) {
      old.stop();
      this.local.removeTrack(old);
    }
    this.local.addTrack(track);
    // The fresh capture answers for itself; the cap on attempts is what keeps
    // this from becoming a loop.
    this.armMicCheck('restart');
  }

  /** Mute, or stop sending a picture. Local and instant; no renegotiation. */
  setTrackEnabled(kind: 'audio' | 'video', enabled: boolean): void {
    for (const track of this.local.getTracks()) {
      if (track.kind === kind) track.enabled = enabled;
    }
    // A microphone muted by its owner is skipped by checkMicrophone — a choice,
    // not a fault — so the moment the choice is reversed is a moment the
    // question has to be asked again, or a sender that died under the mute
    // stays silent with nobody watching.
    if (kind === 'audio' && enabled) this.armMicCheck('unmute');
    // A disabled video track still arrives as a live track at the other end, so
    // the far side would just see a frozen frame. Telling it explicitly is what
    // lets it draw «Камера собеседника выключена» instead.
    if (kind === 'video') this.handlers.onSignal({ kind: 'camera', on: enabled });
  }

  /**
   * Points the camera the other way without interrupting the call.
   *
   * `replaceTrack` swaps what the existing sender is encoding, so there is no
   * new offer, no renegotiation, and no gap in the audio — which shares nothing
   * with the camera and is never touched.
   *
   * The rest of this is one complaint — «нажимаю на переворот, и ничего» — and
   * the three ways a flip has of not happening while looking like it did:
   *
   * A phone that cannot hold both cameras open **refuses** the second one,
   * which used to arrive here as an unhandled rejection and nowhere else; or it
   * does not refuse and hands back **the camera already open**, because
   * `facingMode` is an ideal constraint (see videoConstraints, which says why it
   * has to be). Both are answered the same way, and it is the only order such a
   * device has ever had: close what is open, then ask again with the facing as
   * `exact`, so a camera that will not turn round has to say so.
   *
   * And a **second tap** while that is in flight is dropped rather than queued.
   * The complaint arrives as eight flips in fifteen seconds, and two
   * getUserMedia calls racing for one camera pipeline is how a phone ends up
   * with neither.
   *
   * Rejecting rather than resolving with the old picture is the part the caller
   * cares about: the mirror and the remembered facing are set the moment the
   * button is pressed, and a flip that did not happen has to take them back.
   */
  async flipCamera(facing: Facing): Promise<MediaStream> {
    if (this.flipping) {
      this.handlers.onDiagnostic({
        detail: 'call/camera/flip/busy',
        message: `tap dropped · the ${facing} camera is still opening`,
      });
      throw new Error('a camera flip is already in flight');
    }
    this.flipping = true;
    const started = Date.now();
    try {
      const old = this.local.getVideoTracks()[0] ?? null;
      const before = old === null ? null : cameraOf(old);
      let track = await this.openFacing(facing, old);
      let verdict = flipVerdict(before, cameraOf(track), facing);
      if (verdict === 'same') {
        this.handlers.onDiagnostic({
          detail: 'call/camera/flip/same',
          message: `asked for ${facing} and got ${describeCamera(cameraOf(track))} again · reopening with it closed`,
        });
        track = await this.reopenExact(facing, [old, track]);
        verdict = flipVerdict(before, cameraOf(track), facing);
      }
      await this.adopt(track);
      this.handlers.onDiagnostic({
        detail: verdict === 'turned' ? 'call/camera/flip' : `call/camera/flip/${verdict}`,
        message: `${describeCamera(before)} → ${describeCamera(cameraOf(track))} · asked for ${facing} · ${
          Date.now() - started
        }ms`,
      });
      // Adopted either way: a camera that would not turn round is still a
      // working picture, and the only thing worse than the wrong camera is none.
      if (verdict === 'same') throw new Error('the camera did not turn round');
      return this.local;
    } finally {
      this.flipping = false;
    }
  }

  /**
   * Opens the camera pointing `facing` with the one already open left alone.
   *
   * The safe order, and the one every device that works at all can do: nothing
   * this side is sending is touched until a new camera has actually answered,
   * so a refusal costs the picture nothing.
   */
  private async openFacing(facing: Facing, old: MediaStreamTrack | null): Promise<MediaStreamTrack> {
    try {
      return await openCameraTrack(videoConstraints(this.tuning, facing));
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: 'call/camera/flip/refused',
        message: `${facing} camera refused with the other one open · ${
          error instanceof Error ? error.name : 'unknown'
        } · asking again with it closed`,
      });
      return this.reopenExact(facing, [old]);
    }
  }

  /**
   * The flip a phone with one camera pipeline needs: whatever holds the camera
   * is stopped first, and the facing is asked for as `exact`, so a device that
   * would have quietly handed back the open camera has to turn round or refuse.
   *
   * This is the step onto the ledge — for a moment this side has no camera at
   * all — so a refusal opens the *old* facing again before giving up. A flip
   * that did not happen is a button that did nothing; a camera that never comes
   * back is the black rectangle the far end spends the rest of the call looking
   * at.
   */
  private async reopenExact(
    facing: Facing,
    close: (MediaStreamTrack | null)[],
  ): Promise<MediaStreamTrack> {
    for (const track of close) track?.stop();
    try {
      return await openCameraTrack({
        ...videoConstraints(this.tuning),
        facingMode: { exact: facing },
      });
    } catch (error) {
      this.handlers.onDiagnostic({
        detail: 'call/camera/flip/failed',
        message: `${facing} camera refused with nothing else open · ${
          error instanceof Error ? error.name : 'unknown'
        }`,
      });
      const back = facing === 'user' ? 'environment' : 'user';
      const previous = await openCameraTrack(videoConstraints(this.tuning, back)).catch(() => null);
      if (previous === null) {
        this.handlers.onDiagnostic({
          detail: 'call/camera/lost',
          message: 'neither camera opened after a flip · this side is sending no picture',
        });
        throw error;
      }
      await this.adopt(previous);
      throw error;
    }
  }

  /**
   * Hands the sender a camera and makes the local stream own it — the same
   * three steps restartMicrophone takes for the other device, and in the same
   * order: the sender first, so the far end never sees the gap between one
   * track being stopped and the next being attached.
   */
  private async adopt(track: MediaStreamTrack): Promise<void> {
    const old = this.local.getVideoTracks().find((candidate) => candidate !== track) ?? null;
    // A camera somebody turned off stays off through a flip: the button still
    // says «камера выключена», and a fresh track comes up enabled.
    track.enabled = old?.enabled ?? true;
    this.tune(track);
    // The track a flip replaces was watched from the constructor, and for a
    // long time a flip quietly ended that: a camera the system suspended after
    // one had nobody left to say so to. See watchTrack.
    this.watchTrack(track);
    const sender = this.pc.getSenders().find((candidate) => candidate.track?.kind === 'video');
    await sender?.replaceTrack(track);
    for (const previous of this.local.getVideoTracks()) {
      if (previous === track) continue;
      previous.stop();
      this.local.removeTrack(previous);
    }
    if (!this.local.getVideoTracks().some((candidate) => candidate === track)) {
      this.local.addTrack(track);
    }
  }

  /** The relayed pair carries the call: the experiment is on, said once. */
  private holdRelay(): void {
    this.relayPhase = 'held';
    this.clearRelayTimer();
    this.handlers.onDiagnostic({
      detail: 'call/relay/held',
      message: 'direct path forbidden · connected through the relay',
    });
  }

  /**
   * Lifts the ban on the direct path and offers again with fresh credentials.
   *
   * setConfiguration rather than a new connection: the policy is the one part
   * of a configuration a browser lets change under a live description, and a
   * new connection would be a new call — new tracks, new signalling, a black
   * frame for the far end. The re-offer is the same one a network change
   * makes, minus the count: this is not a network failing, and the budget in
   * MAX_RESTARTS is for those.
   */
  private allowDirect(reason: string): void {
    if (this.closed) return;
    if (this.relayPhase !== 'trying' && this.relayPhase !== 'held') return;
    this.relayPhase = 'fallen';
    this.clearRelayTimer();
    try {
      this.pc.setConfiguration({
        iceServers: this.iceServers,
        iceTransportPolicy: 'all',
        iceCandidatePoolSize: 1,
      });
    } catch (error) {
      // Stuck on the relay policy, and the call may be lost with it — which
      // is why the row names the browser: a refusal here is a fact about
      // which browser it was, not about the network.
      this.handlers.onDiagnostic({
        detail: 'call/relay/fallback',
        message: `${reason} · setConfiguration refused · ${error instanceof Error ? error.name : 'unknown'}`,
      });
      return;
    }
    this.handlers.onDiagnostic({
      detail: 'call/relay/fallback',
      message: `${reason} · direct path allowed again`,
    });
    void this.reoffer();
  }

  private clearRelayTimer(): void {
    if (this.relayTimer === null) return;
    clearTimeout(this.relayTimer);
    this.relayTimer = null;
  }

  /**
   * Asks for a fresh set of paths, after a network change lost the old ones.
   *
   * `restartIce()` is not what does this, and used to be what was here. It
   * raises a flag and fires `negotiationneeded`, and this file has no handler
   * for that event on purpose — so nothing was ever offered and no restart ever
   * happened. What restarts ICE is a new offer carrying new credentials, which
   * is `createOffer({ iceRestart: true })` and is the only form used here. It
   * also has the side benefit of working on the older Safari that has no
   * `restartIce` at all.
   *
   * Only the caller can make an offer. The callee asks for one instead, over
   * the same signalling — and that asymmetry is what stands in for perfect
   * negotiation: both sides notice a walk from Wi-Fi onto mobile data at about
   * the same moment, and two simultaneous offers would be a glare this code has
   * nothing to resolve it with. One side offers, the other asks.
   */
  private restart(): void {
    if (this.closed) return;
    if (this.restarts >= MAX_RESTARTS) {
      // Said once. A connection that fails, restarts and fails again is a
      // network that is not coming back, and a fourth attempt is an offer storm
      // between two phones rather than a recovery.
      if (!this.restartsExhausted) {
        this.restartsExhausted = true;
        this.handlers.onDiagnostic({
          detail: 'call/restart/exhausted',
          message: `${MAX_RESTARTS} ice restarts changed nothing`,
        });
      }
      return;
    }
    this.restarts += 1;
    if (!this.caller) {
      this.handlers.onSignal({ kind: 'restart' });
      return;
    }
    void this.reoffer();
  }

  /** The offer half of a restart, from whichever side asked for it. */
  private async reoffer(): Promise<void> {
    if (this.closed) return;
    try {
      const offer = await this.pc.createOffer({ iceRestart: true });
      await this.pc.setLocalDescription(offer);
      this.handlers.onSignal({ kind: 'offer', sdp: offer });
    } catch (error) {
      // The call is lost, and the UI already says so; this says which browser
      // it was, since that is the only thing separating a refusal here from a
      // network that simply went away.
      this.handlers.onDiagnostic({
        detail: 'call/restart',
        message: `ice restart refused · ${error instanceof Error ? error.name : 'unknown'}`,
      });
    }
  }

  get connectionState(): RTCPeerConnectionState {
    return this.pc.connectionState;
  }

  /** Ends the connection and releases the camera and microphone. Safe twice. */
  close(): void {
    if (this.closed) return;
    this.closed = true;
    if (this.micCheckTimer !== null) {
      clearTimeout(this.micCheckTimer);
      this.micCheckTimer = null;
    }
    this.clearRelayTimer();
    for (const track of this.local.getTracks()) track.stop();
    this.pc.close();
  }
}

/**
 * Why a call could not start, in the words the overlay shows. Separate from
 * capture.ts's version because the remedies differ: a call that cannot open the
 * microphone has nothing to fall back on.
 */
export function callMediaError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return 'Разрешите доступ к камере и микрофону в настройках браузера, чтобы продолжить';
  }
  if (name === 'NotFoundError' || name === 'OverconstrainedError') {
    return 'Камера или микрофон не найдены';
  }
  if (name === 'NotReadableError') return 'Камера или микрофон заняты другим приложением';
  if (!window.isSecureContext) return 'Звонки работают только по HTTPS';
  return error instanceof Error && error.message ? error.message : 'Не удалось начать звонок';
}
